import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Copy, Check, Camera } from 'react-feather';
import './CameraSetup.css';

function CameraSetup() {
  const [searchParams] = useSearchParams();
  const eventId = searchParams.get('eventId');
  
  const [step, setStep] = useState('register'); // 'register' or 'credentials'
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [cameraModel, setCameraModel] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [credentials, setCredentials] = useState(null);
  const [copied, setCopied] = useState({});
  
  useEffect(() => {
    if (!eventId) {
      setError('Invalid setup link. Please scan the QR code again.');
    }
  }, [eventId]);
  
  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch('https://live-event-photos.onrender.com/api/camera-setup/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          eventId,
          name,
          email: email || undefined,
          cameraModel: cameraModel || undefined
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Registration failed');
      }
      
      setCredentials(data);
      setStep('credentials');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied({ ...copied, [field]: true });
      setTimeout(() => {
        setCopied({ ...copied, [field]: false });
      }, 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };
  
  if (!eventId) {
    return (
      <div className="camera-setup-container">
        <div className="camera-setup-card">
          <div className="error-message">{error}</div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="camera-setup-container">
      <div className="camera-setup-card">
        <div className="camera-setup-header">
          <Camera size={48} color="#3B82F6" />
          <h1>Camera FTP Setup</h1>
          <p>Connect your DSLR or mirrorless camera to upload photos automatically</p>
        </div>
        
        {step === 'register' && (
          <form onSubmit={handleRegister} className="camera-setup-form">
            <div className="form-group">
              <label htmlFor="name">Your Name *</label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="John Doe"
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="email">Email (optional)</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="john@example.com"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="camera">Camera Model (optional)</label>
              <select
                id="camera"
                value={cameraModel}
                onChange={(e) => setCameraModel(e.target.value)}
              >
                <option value="">Select your camera</option>
                <option value="canon">Canon (EOS R, 5D, 6D, etc.)</option>
                <option value="nikon">Nikon (Z series, D series)</option>
                <option value="sony">Sony (A7, A9 series)</option>
                <option value="fujifilm">Fujifilm (X series)</option>
                <option value="other">Other</option>
              </select>
            </div>
            
            {error && <div className="error-message">{error}</div>}
            
            <button type="submit" className="submit-button" disabled={loading}>
              {loading ? 'Registering...' : 'Get FTP Credentials'}
            </button>
          </form>
        )}
        
        {step === 'credentials' && credentials && (
          <div className="credentials-section">
            <div className="success-message">
              ✓ Registration successful! Use these credentials in your camera's FTP settings.
            </div>
            
            <div className="event-info">
              <strong>Event:</strong> {credentials.eventName}
            </div>
            
            <div className="credential-item">
              <label>FTP Host</label>
              <div className="credential-value">
                <code>{credentials.ftpHost}</code>
                <button
                  onClick={() => copyToClipboard(credentials.ftpHost, 'host')}
                  className="copy-button"
                >
                  {copied.host ? <Check size={16} /> : <Copy size={16} />}
                </button>
              </div>
            </div>
            
            <div className="credential-item">
              <label>Port</label>
              <div className="credential-value">
                <code>{credentials.ftpPort}</code>
                <button
                  onClick={() => copyToClipboard(credentials.ftpPort.toString(), 'port')}
                  className="copy-button"
                >
                  {copied.port ? <Check size={16} /> : <Copy size={16} />}
                </button>
              </div>
            </div>
            
            <div className="credential-item">
              <label>Username</label>
              <div className="credential-value">
                <code>{credentials.ftpUsername}</code>
                <button
                  onClick={() => copyToClipboard(credentials.ftpUsername, 'username')}
                  className="copy-button"
                >
                  {copied.username ? <Check size={16} /> : <Copy size={16} />}
                </button>
              </div>
            </div>
            
            <div className="credential-item">
              <label>Password</label>
              <div className="credential-value">
                <code>{credentials.ftpPassword}</code>
                <button
                  onClick={() => copyToClipboard(credentials.ftpPassword, 'password')}
                  className="copy-button"
                >
                  {copied.password ? <Check size={16} /> : <Copy size={16} />}
                </button>
              </div>
            </div>
            
            <div className="instructions">
              <h3>Setup Instructions</h3>
              {credentials.cameraModel === 'canon' && (
                <ol>
                  <li>Go to Menu → Communication Settings → FTP Transfer</li>
                  <li>Select "Set FTP Transfer Func." → Enable</li>
                  <li>Enter the credentials above</li>
                  <li>Set "Passive Mode" to ON</li>
                  <li>Test connection and start shooting!</li>
                </ol>
              )}
              {credentials.cameraModel === 'nikon' && (
                <ol>
                  <li>Go to Menu → Network → FTP Upload</li>
                  <li>Select "Connection Settings"</li>
                  <li>Enter the credentials above</li>
                  <li>Enable "Auto Send" for automatic uploads</li>
                  <li>Test connection and start shooting!</li>
                </ol>
              )}
              {credentials.cameraModel === 'sony' && (
                <ol>
                  <li>Go to Menu → Network → FTP Transfer Func.</li>
                  <li>Select "Server Setting"</li>
                  <li>Enter the credentials above</li>
                  <li>Set "Passive Mode" to ON</li>
                  <li>Enable "Auto Transfer" and start shooting!</li>
                </ol>
              )}
              {(!credentials.cameraModel || credentials.cameraModel === 'other' || credentials.cameraModel === 'fujifilm') && (
                <ol>
                  <li>Open your camera's network/FTP settings</li>
                  <li>Enter the credentials shown above</li>
                  <li>Enable passive mode if available</li>
                  <li>Test the connection</li>
                  <li>Start shooting - photos will upload automatically!</li>
                </ol>
              )}
            </div>
            
            <div className="warning-message">
              ⚠️ Save these credentials! You'll need them to configure your camera.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CameraSetup;
